<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import LayoutSearchResultContentsCards from '@/components/contents/LayoutSearchResultContentsCards.vue'
import { searchIndexPath } from '@/generated/railsRoutes'
import { useUserStore } from '@/stores/user'

export default defineComponent({
  name: 'SectionForIndex',

  components: {
    LayoutSearchResultContentsCards,
  },

  props: {
    /**
     * 検索文言
     */
    searchWord: {
      type: String,
      default: '',
    },

    allSearchResult: {
      type: Object as PropType<{ [key in SearchCategory]?: SearchResult }>,
      required: true,
    },

    categories: {
      type: Object as PropType<Record<SearchCategory, { key: SearchCategory, label: string }>>,
      required: true,
    },

    /**
     * クエリパラメータ用の key 情報
     */
    queryKeys: {
      type: Object as PropType<Record<'KEYWORD' | 'FILTER', string>>,
      required: true,
    },
  },

  setup(props) {
    // store
    const userStore = useUserStore()

    /**
     * 全カテゴリの検索結果の中から 1 カテゴリのみを抽出した検索結果
     * @description 未ログイン時は全ての検索結果ではなく、抽出したもののみがレンダリングされる
     */
    const filteredSearchResult = computed(() => {
      return Object.values(props.allSearchResult).find(v => !!v.totalCount)
    })

    /**
     * 各セクションの見出し文言
     * @param key 対象カテゴリーの key
     */
    const headingLabel = (key: SearchCategory) => {
      const _key = key as keyof typeof props.categories
      return props.categories[_key].label
    }

    /**
     * カテゴリー検索結果ページへの遷移パスの生成
     * @param key 対象カテゴリーの key
     */
    const buildPathForMoreLink = (key: SearchCategory) => {
      return props.searchWord
        ? searchIndexPath({ [props.queryKeys.FILTER]: key, [props.queryKeys.KEYWORD]: props.searchWord })
        : searchIndexPath({ [props.queryKeys.FILTER]: key })
    }

    return {
      // store
      userStore,

      // computed
      filteredSearchResult,

      // methods
      headingLabel,
      buildPathForMoreLink,
    }
  },
})
</script>

<template>
  <section class="section">
    <template v-if="userStore.context.isSignedIn">
      <template
        v-for="(result, key) in allSearchResult"
        :key="key">
        <section
          v-if="result && result.totalCount"
          class="section">
          <div class="o-heading__section">
            <h2 class="o-heading__section--item">
              <span class="o-heading__section--label is-primary">
                {{ headingLabel(key) }}
              </span>
              <span class="o-heading__section--label is-secondary">
                ({{ result.totalCount }})
              </span>
            </h2>
          <!-- /.o-heading__section --></div>

          <!-- 「すべて」タブは先頭 3 件のみ表示 -->
          <LayoutSearchResultContentsCards
            :items="result.items.slice(0, 3)"
            :search-word="searchWord" />

          <p class="p">
            <a
              class="a u-action__forward"
              :href="buildPathForMoreLink(key)">
              {{ searchWord ? `${searchWord}で見つかった` : '' }}その他の{{ categories[key].label }}
            </a>
          </p>
        </section>
      </template>
    </template>
    <template v-else-if="filteredSearchResult">
      <!-- 未ログイン状態は先頭 3 件のみ表示 -->
      <LayoutSearchResultContentsCards
        :items="filteredSearchResult.items.slice(0, 3)"
        :search-word="searchWord" />
    </template>
  </section>
</template>

<style lang="sass" scoped>
.o-heading__section
  & &--item
    line-height: 1

  & &--label
    font-weight: bold

    &.is-primary
      font-size: $font-xx-large

    &.is-secondary
      margin-left: 0.3em
</style>
